/**
 * Loss Runs — ask a prior carrier for claims history, and keep asking.
 *
 * ⚠️ THE LIST IS THE PAGE, NOT THE FORM. Sending the first email is the part
 * everybody already manages; what nobody manages is noticing eleven days later
 * that Travelers never answered. So what opens is the outstanding list, ordered
 * by how long it has been waiting, with the form behind a button.
 *
 * ⚠️ WHO IS ASKING IS THE FIRST QUESTION AND IT IS NOT COSMETIC. Josh, 2026-09-02:
 * "it could be from anyone. The new agent, the insured, myself, the aas agent."
 * A carrier reads those four requests completely differently — the insured needs
 * no permission to see their own claims history, a new agent needs the insured's
 * signature. It sets the wording and whether an authorization is prompted for.
 *
 * ⚠️ A MISSING AUTHORIZATION WARNS AND SENDS. Carriers differ, and some release
 * to the agent of record on a phone call. Blocking would be this page inventing
 * a rule the carrier has not made.
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { moduleById } from '@commpass/shared';
import { PageHero } from '../components/PageHero';
import { openedOnForm, prefill } from '../lib/newForm';
import { receiptState } from '../lib/receipt';
import { oneYearAfter } from '../lib/policyTerm';
import { RequestThread } from '../components/RequestThread';
import { StatCard } from '../components/StatCard';
import { useAuth } from '../auth/AuthProvider';
import { ApiError, api, type LossRunRequest } from '../lib/api';
import { formatPhone } from '../lib/phone';

type AskedBy = 'insured' | 'agent_of_record' | 'new_agent' | 'aas';

const ASKED_BY: { value: AskedBy; label: string; hint: string; needsAuth: boolean }[] = [
  { value: 'agent_of_record', label: 'We are the agent of record', hint: 'Usually no authorization needed.', needsAuth: false },
  { value: 'insured', label: 'The insured is asking', hint: 'Their own claims history — no authorization needed.', needsAuth: false },
  { value: 'new_agent', label: 'We are a new agent', hint: 'Most carriers want a signed authorization from the insured.', needsAuth: true },
  { value: 'aas', label: 'Agents Alliance, for a member', hint: 'Depends whether we are agent of record.', needsAuth: false },
];

/**
 * The lines a carrier is most likely to have written. Not exhaustive on
 * purpose — "anything else the carrier needs to know" takes the rest, and a
 * list of thirty checkboxes is a list nobody reads.
 */
const COVERAGE = [
  'General Liability',
  'Property',
  "Workers' Comp",
  'Commercial Auto',
  'Umbrella',
  'Inland Marine',
];

const STATUS_LABEL: Record<LossRunRequest['status'], string> = {
  sent: 'Waiting',
  chased: 'Chased',
  received: 'Received',
  closed: 'Closed',
};

/** Whole days since an ISO timestamp. */
function daysSince(iso: string | null): number {
  if (!iso) return 0;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
}

/** How long a carrier took, or null while we are still waiting. */
function answeredIn(r: LossRunRequest): number | null {
  if (!r.received_at) return null;
  return Math.max(0, Math.round((new Date(r.received_at).getTime() - new Date(r.created_at).getTime()) / 86400000));
}

const longDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

/**
 * Up to two initials for the monogram tiles.
 *
 * ⚠️ A MONOGRAM RATHER THAN A LOGO, DELIBERATELY. The design this came from
 * shows carrier logos; we hold no logo assets and inventing a generic building
 * icon for every row would say less than the carrier's own initials do.
 */
function initialsOf(name: string): string {
  const words = String(name ?? '')
    .replace(/[^A-Za-z0-9 ]/g, ' ')
    .split(/\s+/)
    .filter((w) => w && !/^(the|of|and|inc|llc|ltd|co|company|insurance|group)$/i.test(w));
  const pick = words.length ? words : String(name ?? '').split(/\s+/).filter(Boolean);
  return pick.slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('') || '?';
}

const TABS = [
  { key: 'all', label: 'All requests' },
  { key: 'open', label: 'Outstanding' },
  { key: 'received', label: 'Received' },
  { key: 'closed', label: 'Closed' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

/**
 * The two date inputs, as the carrier reads them.
 *
 * ⚠️ AN <input type="date"> GIVES BACK "2024-01-01" AND A US CARRIER READS
 * 01/01/2024. Formatting from the parts avoids `new Date('2024-01-01')`, which
 * parses as UTC midnight and lands on the last day of December for anybody west
 * of Greenwich — a policy period off by one day in a letter about claim dates.
 */
function policyPeriodText(from: string, to: string): string {
  const us = (iso: string): string => {
    const [y, m, d] = iso.split('-');
    return y && m && d ? `${m}/${d}/${y}` : iso;
  };
  if (from && to) return `${us(from)} – ${us(to)}`;
  if (from) return `from ${us(from)}`;
  if (to) return `to ${us(to)}`;
  return '';
}

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] ?? '');
    r.onerror = () => reject(new Error('Could not read that file.'));
    r.readAsDataURL(file);
  });

/** Where a finished request lands. The list it joins, not the form it came from. */
const REQUESTS_HREF = moduleById('requests')?.href ?? '/hub/requests';

export function LossRuns(): JSX.Element {
  const navigate = useNavigate();
  const { email } = useAuth();

  const [rows, setRows] = useState<LossRunRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  // Straight onto the form when the Requests Center sent you here to make one.
  const [showForm, setShowForm] = useState(openedOnForm);

  // ---- the list ----
  const [tab, setTab] = useState<TabKey>('all');
  const [query, setQuery] = useState('');
  /** Which row has its actions open. The chevron opens this, not a page. */
  const [expanded, setExpanded] = useState('');

  // ---- the form ----
  const [askedBy, setAskedBy] = useState<AskedBy>('agent_of_record');
  /*
    ⚠️ SEEDED FROM THE URL, THEN ORDINARY STATE. Connor hands these over when he
    starts a request for somebody (see lib/newForm.ts). A useState initialiser
    rather than an effect, so the field is filled on the first paint — an effect
    would show it empty and then fill it, which reads as the page correcting
    itself and makes people re-check what they just watched change.
  */
  const [insuredName, setInsuredName] = useState(() => prefill().insured ?? '');
  const [policyNumber, setPolicyNumber] = useState('');
  const [periodFrom, setPeriodFrom] = useState('');
  const [periodTo, setPeriodTo] = useState('');
  /**
   * Has anybody actually chosen an end date?
   *
   * ⚠️ IT EXISTS SO THE AUTO-FILL CANNOT OVERWRITE A DELIBERATE CHOICE. A
   * six-month term typed in, then a correction to the start date, must not
   * silently snap back to twelve months — the whole reason the field is
   * editable is the 0.1% of policies that are not annual, and those are exactly
   * the ones where a quiet overwrite would be wrong.
   */
  const [periodToTouched, setPeriodToTouched] = useState(false);
  /**
   * A request for a quicker turnaround, and the date behind it.
   *
   * Josh, 2026-09-06: "I'd like a checkbox for Urgent where they can put in a
   * proposed need by date." Both travel to the server, which rewrites the one
   * sentence in the letter that asks for delivery — the form never composes
   * letter prose itself, so the preview cannot drift from what sends.
   */
  const [urgent, setUrgent] = useState(false);
  const [neededBy, setNeededBy] = useState('');
  const [carrierOpen, setCarrierOpen] = useState(false);
  /** Whether the letter has been touched, so going back can warn before discarding. */
  const [edited, setEdited] = useState(false);
  const [lines, setLines] = useState<string[]>([]);
  const [yearsWanted, setYearsWanted] = useState('');
  const [carrierName, setCarrierName] = useState(() => prefill().carrier ?? '');
  const [notes, setNotes] = useState('');
  const [replyTo, setReplyTo] = useState('');

  /**
   * The signature block, remembered.
   *
   * ⚠️ NOBODY TYPES THEIR OWN NAME AND TITLE ON EVERY REQUEST. It is the same
   * four values every time, and a form that asks again each time is a form
   * people leave blank — which is exactly the field a carrier needs to decide
   * whether to release the data. Held in localStorage rather than on the user
   * record because an AAS staffer asking on behalf of a member signs as
   * whoever the carrier should recognise, which is not always themselves.
   */
  const [sig, setSig] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('lr-signature') ?? '{}') as Record<string, string>;
    } catch {
      return {} as Record<string, string>;
    }
  });
  const setSigField = (k: string, v: string) => {
    const next = { ...sig, [k]: v };
    setSig(next);
    try {
      localStorage.setItem('lr-signature', JSON.stringify(next));
    } catch {
      // A browser refusing storage must not stop somebody sending a request.
    }
  };

  /**
   * Step 2. Null means we are still filling the form.
   *
   * ⚠️ HELD AS TEXT AND EDITED AS TEXT. The server escapes it on the way out, so
   * there is nothing to inject into an email that leaves under our domain.
   */
  const [draft, setDraft] = useState<{ subject: string; bodyText: string } | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [typedTo, setTypedTo] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [suggested, setSuggested] = useState<{ name: string; email: string; role: string }[]>([]);
  const [matched, setMatched] = useState<string | null>(null);
  const [nearby, setNearby] = useState<{ name: string; hasContact: boolean }[]>([]);

  // Default the carrier's reply to whoever is signed in — they are the one who
  // wants the loss runs. Overridable, because an AAS staffer often asks on
  // behalf of a member and the answer should go to the member.
  useEffect(() => {
    if (email && !replyTo) setReplyTo(email);
  }, [email, replyTo]);

  const load = useCallback(async () => {
    try {
      const r = await api.lossRunList();
      setRows(r.requests);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not load your requests.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Look up carrier contacts as the name is typed. Debounced, because this runs
   * on every keystroke and the answer only matters once they stop.
   */
  useEffect(() => {
    if (!carrierName.trim()) {
      setSuggested([]);
      setMatched(null);
      setNearby([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const r = await api.lossRunContacts(carrierName);
        setSuggested(r.contacts);
        setMatched(r.matched ?? null);
        setNearby(r.carriers ?? []);
      } catch {
        // A failed lookup must not stop somebody typing an address by hand.
        setSuggested([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [carrierName]);

  const recipients = useMemo(() => {
    const typed = typedTo
      .split(/[,;\s]+/)
      .map((s) => s.trim())
      .filter((s) => s.includes('@'));
    return [...new Set([...picked, ...typed])];
  }, [picked, typedTo]);

  const needsAuth = ASKED_BY.find((a) => a.value === askedBy)?.needsAuth ?? false;
  const canSend = insuredName.trim() && carrierName.trim() && recipients.length > 0 && replyTo.includes('@');

  /** Everything the letter needs, in one place, so preview and send agree. */
  const letterFields = () => ({
    askedBy,
    insuredName: insuredName.trim(),
    policyNumber: policyNumber.trim(),
    policyPeriod: policyPeriodText(periodFrom, periodTo),
    linesOfCoverage: lines,
    yearsWanted: yearsWanted.trim(),
    requesterName: (sig.name ?? '').trim(),
    requesterTitle: (sig.title ?? '').trim(),
    requesterPhone: (sig.phone ?? '').trim(),
    agencyName: (sig.agency ?? '').trim(),
    replyTo: replyTo.trim(),
    notes: notes.trim(),
    urgent,
    // Only meaningful when urgent; sent empty otherwise so a stale date cannot leak.
    neededBy: urgent ? neededBy : '',
  });

  /** Form → letter. Renders server-side, so what is shown is what will send. */
  async function toReview() {
    setBusy('preview');
    setErr('');
    try {
      setDraft(await api.lossRunPreview({ ...letterFields(), attachmentCount: files.length }));
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not build the letter.');
    } finally {
      setBusy('');
    }
  }

  /**
   * Back to the form, discarding edits.
   *
   * ⚠️ ASKS FIRST IF THE LETTER WAS TOUCHED. Going back regenerates it from the
   * fields — which is the point — but somebody who has just rewritten three
   * paragraphs should not lose them to a mis-click.
   */
  function backToForm() {
    if (edited && !window.confirm('Your changes to the letter will be lost. Go back?')) return;
    setDraft(null);
    setEdited(false);
  }

  const open = rows.filter((r) => r.status === 'sent' || r.status === 'chased');

  /**
   * Four numbers, no chart.
   *
   * ⚠️ THE JOB HERE IS A HEADLINE, NOT A TREND. Nobody needs a plot of loss run
   * requests over time; they need to know whether anything is late and whether
   * chasing works. A stat tile answers both in a glance, and a chart of a
   * dozen rows would be decoration pretending to be analysis.
   *
   * `longest` is the one that earns its place — an average hides the request
   * that has been sitting for three weeks, which is the only one anybody has to
   * do something about.
   */
  /** The tab, then the search, then newest first. */
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows
      .filter((r) =>
        tab === 'all'
          ? true
          : tab === 'open'
            ? r.status === 'sent' || r.status === 'chased'
            : r.status === tab,
      )
      .filter(
        (r) =>
          !q ||
          `${r.insured_name} ${r.carrier_name} ${r.id} ${r.policy_number}`.toLowerCase().includes(q),
      )
      .sort((a, b) => b.created_at.localeCompare(a.created_at));
  }, [rows, tab, query]);


  const stats = useMemo(() => {
    const waits = open.map((r) => daysSince(r.created_at));
    const answered = rows
      .filter((r) => r.status === 'received' && r.received_at)
      .map((r) => Math.max(0, daysSince(r.created_at) - daysSince(r.received_at)));
    const median = answered.length
      ? [...answered].sort((a, b) => a - b)[Math.floor(answered.length / 2)] ?? 0
      : null;
    return {
      outstanding: open.length,
      longest: waits.length ? Math.max(...waits) : 0,
      received: rows.filter((r) => r.status === 'received').length,
      median,
    };
  }, [rows, open]);

  async function send() {
    if (!canSend) return;
    setBusy('send');
    setErr('');
    try {
      const payload = await Promise.all(
        files.map(async (f) => ({ name: f.name, mime: f.type || 'application/octet-stream', data: await fileToBase64(f), size: f.size })),
      );
      const r = await api.lossRunCreate({
        ...letterFields(),
        carrierName: (matched ?? carrierName).trim(),
        sendTo: recipients,
        files: payload,
        // What was on screen when they pressed send, not a re-render of it.
        ...(draft ? { bodyText: draft.bodyText, subject: draft.subject } : {}),
      });
      /*
        ⚠️ THE PAGE ENDS HERE (Josh, 2026-09-05: "when you finish a request, it
        takes you back to the Requests Center so you can see the sent request in
        the dashboard"). The receipt travels rather than being shown in place,
        because the reference is how the carrier's reply gets routed back and
        the authorization warning is the likeliest reason this never gets
        answered — neither survives simply navigating away.
      */
      navigate(
        REQUESTS_HREF,
        receiptState({
          id: r.id,
          headline: `Loss run request sent to ${(matched ?? carrierName).trim()}. The carrier’s reply goes to ${replyTo}.`,
          ...(r.authorizationExpected
            ? {
                warn: 'No authorization was attached. Most carriers will not release loss runs to a new agent without one — if this comes back refused, that is why.',
              }
            : {}),
        }),
      );
      setShowForm(false);
      setDraft(null);
      setEdited(false);
      setInsuredName('');
      setPolicyNumber('');
      setPeriodFrom('');
      setPeriodTo('');
      setPeriodToTouched(false);
      setUrgent(false);
      setNeededBy('');
      setLines([]);
      setYearsWanted('');
      setCarrierName('');
      setNotes('');
      setPicked([]);
      setTypedTo('');
      setFiles([]);
      await load();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not send it.');
    } finally {
      setBusy('');
    }
  }

  async function chase(id: string) {
    setBusy(id);
    setErr('');
    try {
      await api.lossRunChase(id);
      await load();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not send the follow-up.');
    } finally {
      setBusy('');
    }
  }

  async function resolve(id: string, outcome: 'received' | 'closed') {
    setBusy(id);
    setErr('');
    try {
      await api.lossRunResolve(id, outcome);
      await load();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not update it.');
    } finally {
      setBusy('');
    }
  }

  return (
    <div className="wrap">
      {/*
        ⚠️ A DEEP HERO HERE, NOT THE SHALLOW STRIP THE OTHER PAGES USE. The
        strip exists because a title on a 190px band of sunset was unreadable —
        but that was a text problem, not an image problem. Given real depth and
        a gradient that actually darkens the left, the title reads fine and the
        page gains a front-door feel the list pages do not need.
      */}
      <PageHero
        title="Loss runs"
        sub="Request and track loss runs from prior carriers."
        action={
          <button
            className="btn btn-primary"
            type="button"
            onClick={() => {
              if (showForm) {
                setDraft(null);
                setEdited(false);
              }
              setShowForm((s) => !s);
            }}
          >
            {showForm ? 'Cancel' : 'New request'}
          </button>
        }
      />

      {err && <p className="is-warn">{err}</p>}


      {showForm && (
        <ol className="lr-steps">
          <li className={draft ? '' : 'is-on'}>1 · The details</li>
          <li className={draft ? 'is-on' : ''}>2 · The letter</li>
        </ol>
      )}

      {showForm && !draft && (
        <section className="panel lr-form">
          <p className="lr-legend">Who is asking?</p>
          <div className="lr-who">
            {ASKED_BY.map((a) => (
              <label key={a.value} className={askedBy === a.value ? 'is-on' : ''}>
                <input type="radio" name="askedBy" checked={askedBy === a.value} onChange={() => setAskedBy(a.value)} />
                <span className="lr-who-label">{a.label}</span>
                <span className="lr-who-hint">{a.hint}</span>
              </label>
            ))}
          </div>

          {/* ⚠️ HELP TEXT GOES UNDER THE INPUT, NOT BETWEEN LABEL AND INPUT.
              The shared .sf-field puts it in the middle, which is fine on a form
              where every field has one — and misaligns every row here, because a
              field with help pushed its input a line below its neighbour's. */}
          <div className="sf-fields lr-fields">
            <div className="sf-field">
              <label htmlFor="lr-insured">Named insured</label>
              <input
                id="lr-insured"
                type="text"
                value={insuredName}
                onChange={(e) => setInsuredName(e.target.value)}
                placeholder="ACME Contracting LLC"
              />
              <p className="sf-help">Exactly as it reads on the policy.</p>
            </div>
            <div className="sf-field">
              <label htmlFor="lr-policy">Policy number(s)</label>
              <input
                id="lr-policy"
                type="text"
                value={policyNumber}
                onChange={(e) => setPolicyNumber(e.target.value)}
                placeholder="ABC-123456, or ABC-123456 and all previous"
              />
              {/*
                ⚠️ IT NOW SAYS OUT LOUD THAT A PARTIAL ANSWER IS ACCEPTED. The
                field was already free text and already optional, so nothing was
                blocked — but "If you have it" told an agent nothing about what
                to do when they have one number and know there were others.
                Loss Run Pro learned the same thing (2026-09-10): "a lot of times
                insureds only know that most recent", so their requests carry
                "all previous" or "all that pertain to the policy" and carriers
                work from it.

                The failure this prevents is quiet: an agent with only the
                current number either leaves the field blank, which gives the
                desk nothing to search on, or waits until they can dig out the
                expiring one — which is the request not going out today.
              */}
              <p className="sf-help">
                One is enough. If you only have the current one, say so — “and all previous”, or
                “and any others for this insured”, is normal and carriers work from it.
              </p>
            </div>
            {/* ⚠️ A LIST, NOT A CLOSED SELECT. Loss runs are requested from the
                PRIOR carrier, which is often one we are not appointed with at
                all — a direct writer, or the market the account is leaving. A
                dropdown that refused anything unlisted would block the most
                common case there is. So: our 418 carriers offered, anything
                accepted. */}
            <div className="sf-field lr-combo">
              <label htmlFor="lr-carrier">Carrier</label>
              <input
                id="lr-carrier"
                type="text"
                autoComplete="off"
                value={carrierName}
                onChange={(e) => {
                  setCarrierName(e.target.value);
                  setCarrierOpen(true);
                }}
                onFocus={() => setCarrierOpen(true)}
                // A click on an option would otherwise be lost to the blur that
                // closes the list before the click lands.
                onBlur={() => setTimeout(() => setCarrierOpen(false), 150)}
                placeholder="Start typing, or pick from the list"
              />
              {carrierOpen && nearby.length > 0 && (
                <ul className="lr-options">
                  {nearby.map((c) => (
                    <li key={c.name}>
                      <button
                        type="button"
                        onClick={() => {
                          setCarrierName(c.name);
                          setCarrierOpen(false);
                        }}
                      >
                        <span>{c.name}</span>
                        {c.hasContact && <span className="lr-hascontact">contact</span>}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <p className="sf-help">Ours are listed — but any carrier can be typed.</p>
            </div>
            <div className="sf-field">
              <span className="lr-sublabel">Policy period</span>
              <div className="lr-dates">
                <input
                  type="date"
                  aria-label="Policy period start"
                  value={periodFrom}
                  onChange={(e) => {
                    setPeriodFrom(e.target.value);
                    // Josh, 2026-09-06: "99.9% of these policies will be 12
                    // month policies. If they're not, they can manually change."
                    if (!periodToTouched) setPeriodTo(oneYearAfter(e.target.value));
                  }}
                />
                <span>to</span>
                <input
                  type="date"
                  aria-label="Policy period end"
                  value={periodTo}
                  onChange={(e) => {
                    setPeriodTo(e.target.value);
                    setPeriodToTouched(true);
                  }}
                />
              </div>
              <p className="sf-help">Which term, if you know it. The end date fills in a year on — change it if the term is not annual.</p>
            </div>
            {/*
              ⚠️ THE DISCLAIMER IS THE POINT OF THE FEATURE, NOT DECORATION AROUND
              IT (Josh, 2026-09-06: "Make a little disclaimer when agents select
              that saying this is simply requesting a quicker turnaround. It
              doesn't guarantee they'll be able to accomodate."). A tick box
              labelled "Urgent" invites an agent to believe they have bought
              something. They have asked for something — from a desk that owes us
              nothing, often at a carrier we no longer place with — and if that
              belief travels back to an insured as a promised date, the agent
              wears it. So it appears the moment the box is ticked, not in help
              text somebody may never open.
            */}
            <div className="sf-field lr-urgent">
              <label className="lr-check">
                <input
                  type="checkbox"
                  checked={urgent}
                  onChange={(e) => {
                    setUrgent(e.target.checked);
                    if (!e.target.checked) setNeededBy('');
                  }}
                />
                <span>Urgent — ask for a quicker turnaround</span>
              </label>

              {urgent && (
                <div className="lr-urgent-body">
                  <label htmlFor="lr-needed">Needed by</label>
                  <input
                    id="lr-needed"
                    type="date"
                    value={neededBy}
                    onChange={(e) => setNeededBy(e.target.value)}
                  />
                  <p className="lr-urgent-note">
                    This asks the carrier to prioritise the request and names your date in the
                    letter. It is a request, not a commitment — the carrier may not be able to
                    accommodate it, so don&rsquo;t promise this date to an insured on the strength
                    of ticking this box.
                  </p>
                </div>
              )}
            </div>
            <div className="sf-field">
              <label htmlFor="lr-years">Years wanted</label>
              <input
                id="lr-years"
                type="text"
                value={yearsWanted}
                onChange={(e) => setYearsWanted(e.target.value)}
                placeholder="5"
              />
              <p className="sf-help">How far back. Defaults to 5.</p>
            </div>
            <div className="sf-field">
              {/* Not a <label for>: this heads a group of checkboxes, and pointing
                  it at one of them would make the whole heading toggle that box. */}
              <span className="lr-sublabel">Lines of coverage</span>
              <div className="lr-lines">
                {COVERAGE.map((c) => (
                  <label key={c} className={lines.includes(c) ? 'is-on' : ''}>
                    <input
                      type="checkbox"
                      checked={lines.includes(c)}
                      onChange={(e) => setLines((p) => (e.target.checked ? [...p, c] : p.filter((x) => x !== c)))}
                    />
                    {c}
                  </label>
                ))}
              </div>
              <p className="sf-help">A carrier may write several — say which.</p>
            </div>
          </div>

          <div className="lr-contacts">
            <p className="lr-legend">Send to</p>
            {suggested.length > 0 ? (
              <>
                <p className="sf-help">
                  From our contact book for <b>{matched}</b>. One or two is plenty.
                </p>
                {suggested.map((c) => (
                  <label key={c.email} className="lr-contact">
                    <input
                      type="checkbox"
                      checked={picked.includes(c.email)}
                      onChange={(e) =>
                        setPicked((p) => (e.target.checked ? [...p, c.email] : p.filter((x) => x !== c.email)))
                      }
                    />
                    <span>
                      <b>{c.name || c.email}</b> <span className="lr-role">{c.role}</span>
                      <br />
                      <span className="sf-help">{c.email}</span>
                    </span>
                  </label>
                ))}
              </>
            ) : (
              <p className="sf-help">
                {carrierName.trim()
                  ? 'We hold no loss run contact for that carrier yet — type the address below.'
                  : 'Type a carrier above and we will suggest who to ask.'}
              </p>
            )}
            <div className="sf-field is-wide">
              <label htmlFor="lr-other">Other addresses</label>
              <p className="sf-help">Comma separated.</p>
              <input
                id="lr-other"
                type="text"
                value={typedTo}
                onChange={(e) => setTypedTo(e.target.value)}
                placeholder="lossruns@carrier.com"
              />
            </div>
          </div>

          <div className="sf-fields lr-fields">
            <div className="sf-field">
              <label htmlFor="lr-replyto">Send the reports to</label>
              <input id="lr-replyto" type="text" value={replyTo} onChange={(e) => setReplyTo(e.target.value)} />
              <p className="sf-help">Where the carrier replies.</p>
            </div>
            <div className="sf-field">
              <span className="lr-sublabel">{needsAuth ? 'Signed authorization' : 'Attachments'}</span>
              {/* A real button. The native file input reads as a browser control
                  rather than part of the form, and says "No file chosen" in a
                  place where nothing is required. */}
              <label className="lr-file">
                <input
                  type="file"
                  multiple
                  onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
                />
                <span className="btn btn-ghost btn-sm">Attach a file</span>
              </label>
              {files.length > 0 ? (
                <ul className="lr-filelist">
                  {files.map((f) => (
                    <li key={f.name}>
                      <span>{f.name}</span>
                      <button
                        type="button"
                        aria-label={`Remove ${f.name}`}
                        onClick={() => setFiles((p) => p.filter((x) => x.name !== f.name))}
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="sf-help">{needsAuth ? 'Most carriers will want one.' : 'Optional.'}</p>
              )}
            </div>
            <div className="sf-field is-wide is-textarea">
              <label htmlFor="lr-notes">Anything else the carrier needs to know</label>
              <textarea id="lr-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
          </div>

          <p className="lr-legend">Who is signing it</p>
          <p className="sf-help lr-sighint">Remembered on this device, so you only type it once.</p>
          <div className="sf-fields lr-fields">
            <div className="sf-field">
              <label htmlFor="lr-signame">Your name</label>
              <input id="lr-signame" type="text" value={sig.name ?? ''} onChange={(e) => setSigField('name', e.target.value)} />
            </div>
            <div className="sf-field">
              <label htmlFor="lr-sigtitle">Title</label>
              <input id="lr-sigtitle" type="text" value={sig.title ?? ''} onChange={(e) => setSigField('title', e.target.value)} />
            </div>
            <div className="sf-field">
              <label htmlFor="lr-sigagency">Agency</label>
              <input id="lr-sigagency" type="text" value={sig.agency ?? ''} onChange={(e) => setSigField('agency', e.target.value)} />
            </div>
            <div className="sf-field">
              <label htmlFor="lr-sigphone">Phone</label>
              <input
                id="lr-sigphone"
                type="tel"
                value={sig.phone ?? ''}
                onChange={(e) => setSigField('phone', e.target.value)}
                onBlur={(e) => setSigField('phone', formatPhone(e.target.value))}
              />
              <p className="sf-help">So a query does not cost another email.</p>
            </div>
          </div>

          {needsAuth && !files.length && (
            <p className="is-warn">
              A new agent usually needs a signed authorization from the insured. You can send without one —
              the letter simply will not claim there is one attached.
            </p>
          )}

          <div className="lr-send">
            <button className="btn btn-primary" type="button" disabled={!canSend || busy === 'preview'} onClick={toReview}>
              {busy === 'preview' ? 'Building…' : canSend ? 'Next — review the letter' : 'Fill in the details above'}
            </button>
          </div>
        </section>
      )}

      {showForm && draft && (
        <section className="panel lr-review">
          <div className="lr-meta">
            <div>
              <span>To</span>
              <b>{recipients.join(', ')}</b>
            </div>
            <div>
              <span>Replies go to</span>
              <b>{replyTo}</b>
            </div>
            {files.length > 0 && (
              <div>
                <span>Attached</span>
                <b>{files.map((f) => f.name).join(', ')}</b>
              </div>
            )}
          </div>

          <div className="sf-field lr-fields">
            <label htmlFor="lr-subject">Subject</label>
            <input
              id="lr-subject"
              type="text"
              value={draft.subject}
              onChange={(e) => {
                setDraft({ ...draft, subject: e.target.value });
                setEdited(true);
              }}
            />
          </div>

          <div className="sf-field lr-fields">
            <label htmlFor="lr-body">The letter</label>
            <textarea
              id="lr-body"
              className="lr-body"
              rows={22}
              value={draft.bodyText}
              onChange={(e) => {
                setDraft({ ...draft, bodyText: e.target.value });
                setEdited(true);
              }}
            />
            {/* Shown, not editable. Deleting it would break reply routing, so it
                is never in the box — see the note on referenceFooter. */}
            <p className="sf-help lr-footnote">
              A reference line is added underneath so the carrier’s reply finds its way back to you.
            </p>
          </div>

          {needsAuth && !files.length && (
            <p className="is-warn">
              No authorization is attached, so the letter does not mention one. Most carriers will want it
              from a new agent.
            </p>
          )}

          <div className="lr-send">
            <button className="btn btn-ghost" type="button" onClick={backToForm}>
              Back
            </button>
            <button className="btn btn-primary" type="button" disabled={!canSend || busy === 'send'} onClick={send}>
              {busy === 'send' ? 'Sending…' : `Send to ${recipients.length}`}
            </button>
          </div>
        </section>
      )}

      {!showForm && (
        <>
          <div className="stat-row">
            <StatCard
              tone="blue"
              icon="M4 20V8l7-4 7 4v12M9 20v-5h4v5M8 11h.01M12 11h.01M16 11h.01"
              label="Outstanding"
              value={stats.outstanding}
              note="Requests awaiting response"
              loading={loading}
            />
            <StatCard
              tone="green"
              icon="M12 3a9 9 0 100 18 9 9 0 000-18zM8 12l3 3 5-5"
              label="Received"
              value={stats.received}
              note="Loss runs received"
              loading={loading}
            />
            {/* ⚠️ `Longest wait` RATHER THAN AN AVERAGE. A mean hides the request
                that has been sitting three weeks, which is the only one anybody
                has to act on. */}
            <StatCard
              tone="violet"
              icon="M12 7v5l3 2M12 3a9 9 0 100 18 9 9 0 000-18z"
              label="Longest wait"
              value={stats.outstanding ? stats.longest : '—'}
              unit={stats.outstanding > 0 ? (stats.longest === 1 ? 'day' : 'days') : undefined}
              note={stats.outstanding ? 'Oldest still open' : 'Nothing waiting'}
              loading={loading}
              slow={stats.longest >= 7}
              late={stats.longest >= 14}
            />
            <StatCard
              tone="slate"
              icon="M6 3h9l4 4v14H6zM14 3v5h5M9 13h7M9 17h5"
              label="Total requests"
              value={rows.length}
              note="All time"
              loading={loading}
            />
          </div>

          <section className="panel lr-table-panel">
            <div className="lr-toolbar">
              <div className="lr-tabs" role="tablist">
                {TABS.map((t) => (
                  <button
                    key={t.key}
                    role="tab"
                    type="button"
                    aria-selected={tab === t.key}
                    className={tab === t.key ? 'is-on' : ''}
                    onClick={() => setTab(t.key)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <div className="lr-search">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M11 4a7 7 0 100 14 7 7 0 000-14zM20 20l-4-4" />
                </svg>
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search loss runs…"
                  aria-label="Search loss runs"
                />
              </div>
            </div>

            {loading ? (
              <p className="lr-empty">Loading…</p>
            ) : !visible.length ? (
              <p className="lr-empty">
                {rows.length ? 'Nothing matches that.' : 'No requests yet.'}
                <span>
                  {rows.length
                    ? 'Try another tab, or clear the search.'
                    : 'Send one and it appears here, following itself up until the carrier answers.'}
                </span>
              </p>
            ) : (
              <>
                <table className="lr-table">
                  <thead>
                    <tr>
                      <th>Insured</th>
                      <th>Prior carrier</th>
                      <th>Requested</th>
                      <th>Status</th>
                      <th>Response time</th>
                      <th aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((r) => {
                      const isOpen = r.status === 'sent' || r.status === 'chased';
                      const waited = isOpen ? daysSince(r.created_at) : answeredIn(r);
                      return (
                        <Fragment key={r.id}>
                        <tr className={expanded === r.id ? 'is-expanded' : ''}>
                          <td>
                            <div className="lr-cell">
                              <span className="lr-avatar" aria-hidden="true">
                                {initialsOf(r.insured_name)}
                              </span>
                              <div>
                                <b>{r.insured_name}</b>
                                <span>{r.id}</span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="lr-cell">
                              {/* ⚠️ A MONOGRAM, NOT A LOGO. We hold no carrier
                                  logo assets, and a generic placeholder icon on
                                  every row says less than the carrier's own
                                  initials do. */}
                              <span className="lr-avatar is-carrier" aria-hidden="true">
                                {initialsOf(r.carrier_name)}
                              </span>
                              <b>{r.carrier_name}</b>
                            </div>
                          </td>
                          <td>{longDate(r.created_at)}</td>
                          <td>
                            <span className={`pill lr-${r.status}`}>{STATUS_LABEL[r.status]}</span>
                            {r.received_at && <div className="lr-sub">{longDate(r.received_at)}</div>}
                          </td>
                          <td>
                            {waited === null ? '—' : `${waited} ${waited === 1 ? 'day' : 'days'}`}
                          </td>
                          <td className="lr-chev">
                            {/* The chevron opens the actions rather than a page
                                that does not exist — a control that goes nowhere
                                is worse than no control. */}
                            <button
                              type="button"
                              aria-label={expanded === r.id ? 'Hide actions' : 'Show actions'}
                              aria-expanded={expanded === r.id}
                              onClick={() => setExpanded(expanded === r.id ? '' : r.id)}
                            >
                              <svg viewBox="0 0 24 24" aria-hidden="true">
                                <path d="M9 6l6 6-6 6" />
                              </svg>
                            </button>
                          </td>
                        </tr>

                        {/*
                          ⚠️ A ROW INSIDE THE TABLE, NOT A DIV AFTER IT. This
                          drawer used to render below </table>, keyed off a
                          single `expandedRow` — so opening the chevron on the
                          first request showed its details underneath the LAST
                          one, which reads as the wrong request's data rather
                          than as a misplaced panel. It has to be a sibling <tr>
                          of the row it belongs to, or the table lays it out
                          wherever it happens to sit.
                        */}
                        {expanded === r.id && (
                          <tr className="lr-drawer-row">
                            <td colSpan={6}>
                              <div className="lr-drawer">
                                <div>
                                  <span>Sent to</span>
                                  <b>{r.sent_to.join(', ') || '—'}</b>
                                </div>
                                <div>
                                  <span>Replies to</span>
                                  <b>{r.reply_to}</b>
                                </div>
                                {r.chase_count > 0 && (
                                  <div>
                                    <span>Chased</span>
                                    <b>{r.chase_count}×</b>
                                  </div>
                                )}
                                {isOpen && (
                                  <div className="lr-drawer-actions">
                                    <button
                                      className="btn btn-sm"
                                      type="button"
                                      disabled={busy === r.id}
                                      onClick={() => chase(r.id)}
                                    >
                                      Follow up
                                    </button>
                                    <button
                                      className="btn btn-sm btn-primary"
                                      type="button"
                                      disabled={busy === r.id}
                                      onClick={() => resolve(r.id, 'received')}
                                    >
                                      Got it
                                    </button>
                                    <button
                                      className="btn btn-sm btn-ghost"
                                      type="button"
                                      disabled={busy === r.id}
                                      onClick={() => resolve(r.id, 'closed')}
                                    >
                                      Give up
                                    </button>
                                  </div>
                                )}
                              </div>
                              {/*
                                ⚠️ MOUNTED ONLY WHILE THE DRAWER IS OPEN, WHICH
                                IS WHY IT IS IN HERE RATHER THAN ABOVE. It fetches
                                on mount; rendering one per row would fire a
                                request for every request on the page the moment
                                the list loaded, to show something nobody has
                                asked to see.
                              */}
                              <RequestThread reference={r.id} />
                            </td>
                          </tr>
                        )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>

                <p className="lr-count-line">
                  Showing {visible.length} of {rows.length} request{rows.length === 1 ? '' : 's'}
                </p>
              </>
            )}
          </section>

          <section className="panel lr-about">
            <div>
              <h3>
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 3a9 9 0 100 18 9 9 0 000-18zM12 11v5M12 8h.01" />
                </svg>
                About loss runs
              </h3>
              <p>
                A loss run is the carrier’s own record of claims activity on a policy. Underwriters
                ask for them on almost every renewal and most new business, and a submission without
                them usually waits.
              </p>
            </div>
            <div>
              <h3>Getting them back faster</h3>
              <ul>
                <li>Give the policy number and the policy period — a name alone means a search.</li>
                <li>Send it to the loss run desk where we hold one, not an underwriter.</li>
                <li>Allow five to ten business days before expecting an answer.</li>
                <li>A new agent needs the insured’s signed authorization attached.</li>
              </ul>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
